import React from 'react';
import {
  Header,
  SpaceBetween,
  Box,
  TextContent,
  ColumnLayout,
  Button,
  Link,
  Alert,
  AnchorNavigation,
  AppLayout,
  ContentLayout,
  Grid,
  Table,
} from '@cloudscape-design/components';
import MermaidDiagram from '../../components/MermaidDiagram';
import { useTheme } from '../../context/ThemeContext';
import { useModuleVisit } from '../../hooks/useAnalytics';

const DEFAULT_BANNER = "linear-gradient(135deg, rgb(71, 17, 118) 3%, rgb(131, 57, 157) 44%, rgb(149, 85, 182) 69%, rgb(145, 134, 215) 94%)";
const SUMMIT_BANNER_LIGHT = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_1@2x.png')";
const SUMMIT_BANNER_DARK = "center/cover url('/summit/2025_Summits_Kaltura_SessionPage_SessionThumbnail_2.png')";

// Main ArchitecturePageContent component
function ArchitecturePageContent() {
  const { isSummit, isDark } = useTheme();
  useModuleVisit('architecture');
  const banner = isSummit ? (isDark ? SUMMIT_BANNER_DARK : SUMMIT_BANNER_LIGHT) : DEFAULT_BANNER;
  return (
    <ContentLayout
      defaultPadding
      disableOverlap
      headerBackgroundStyle={banner}
      headerVariant="high-contrast"
      header={
        <Box padding={{ vertical: "xxxl" }}>
          <Grid
            gridDefinition={[
              { colspan: { default: 12, s: 8 } }
            ]}
          >
            <Box padding="s">
              <Box
                fontSize="display-l"
                fontWeight="bold"
                variant="h1"
                padding="n"
              >
                System Architecture
              </Box>
              <Box
                variant="p"
                color="text-body-secondary"
                margin={{ top: "xs", bottom: "l" }}
              >
                ResearchIQ is built on a serverless architecture using AWS services, providing 
                scalability, reliability, and cost-effectiveness for financial document analysis.
              </Box>
            </Box>
          </Grid>
        </Box>
      }
    >
      <SpaceBetween size="xxl">
        {/* Overview Section */}
        <div id="overview">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <Header variant="h1" margin={{ bottom: "l" }}>
              Overview
            </Header>
          </Box>
          
          <TextContent>
            <p>
              <Link external href="https://aws.amazon.com/fargate/">AWS Fargate</Link> is a serverless compute engine for containers that works with <Link external href="https://aws.amazon.com/ecs/">Amazon ECS</Link> and <Link external href="https://aws.amazon.com/eks/">EKS</Link>. 
              It allows you to run containers without having to manage servers or clusters. This makes it an 
              excellent choice for deploying <Link href="/deep-dive/strands-agents">Strands AI agents</Link> as containerized applications with high 
              availability and scalability.
            </p>
          </TextContent>

          <Box margin={{ vertical: "l" }}>
            <MermaidDiagram
              id="researchiq-architecture"
              chart={`
                graph TD
                  User[Analyst / Quick / MCP Client]

                  subgraph Auth[Authentication]
                    Cognito[Amazon Cognito - Midway Federation]
                  end

                  subgraph FE[Frontend - ECS Fargate]
                    React[React App - Cloudscape UI]
                  end

                  subgraph BE[Backend - ECS Fargate x2 HA]
                    FastAPI[FastAPI + WebSocket]
                    Strands[Strands Agents Graph]
                    DR[Document Researcher - Haiku 4.5]
                    EC[Evidence Compiler - Nova Pro]
                    CT[Citation Tracker - Nova Pro]
                    Insights[Insights Agent - Nova Pro async]
                    ExecBrief[Exec Brief x4 parallel - GPT-OSS 120B]
                    MCP[MCP Server - optional]
                  end

                  subgraph Persist[Persistence]
                    DDB[DynamoDB - Session Store 24h TTL]
                  end

                  subgraph Bedrock[Amazon Bedrock]
                    Models[Foundation Models]
                    KB[Knowledge Base - OpenSearch + S3]
                  end

                  User -->|HTTPS| Cognito
                  Cognito --> React
                  React -->|REST + WebSocket| FastAPI
                  FastAPI --> Strands
                  Strands --> DR --> EC --> CT
                  CT --> Insights
                  CT --> ExecBrief
                  FastAPI <-->|Session state| DDB
                  DR --> Models
                  EC --> Models
                  CT --> Models
                  Insights --> Models
                  ExecBrief --> Models
                  DR -->|Retrieve| KB
                  MCP -.->|Optional| FastAPI

                  style DR fill:#232f3e,stroke:#fff,color:#fff
                  style EC fill:#ff9900,stroke:#fff,color:#fff
                  style CT fill:#146eb4,stroke:#fff,color:#fff
                  style Insights fill:#1d8348,stroke:#fff,color:#fff
                  style ExecBrief fill:#8b5cf6,stroke:#fff,color:#fff
                  style MCP fill:#545b64,stroke:#aaa,color:#fff
              `}
            />
          </Box>

          <Box margin={{ vertical: "l" }}>
            <TextContent>
              <ol>
                <li>Analyst <strong>places questions</strong> via the web UI, or an enterprise AI tool calls via the <strong>MCP Server</strong>.</li>
                <li>Request authenticated via <strong>Amazon Cognito</strong> (Midway federation for internal users).</li>
                <li><strong><Link href="/deep-dive/strands-agents">Strands Agents</Link></strong> invoked with question array as the input.</li>
                <li>Agent <strong>graph processing</strong> — 6 questions processed in parallel, each through a 3-agent sequential pipeline (Researcher → Compiler → Tracker).</li>
                <li><strong>IAM Role</strong> gives permission for agents inside the ECS container to access other AWS services.</li>
                <li><strong>DynamoDB</strong> stores session state with 24-hour TTL — shared across both backend containers for high availability.</li>
                <li><strong>Bedrock</strong> invoked with the <Link href="/deep-dive/model-strategy">right model per agent</Link> — Haiku for research, Nova Pro for formatting, GPT-OSS for exec brief synthesis.</li>
                <li>Bedrock <strong>Knowledge Base</strong> uses Amazon OpenSearch as its vector store for semantic search, with source documents stored in S3.</li>
                <li>After research completes, the <strong>Insights Agent</strong> runs async to identify cross-question themes and risks.</li>
                <li>The <strong>Exec Brief</strong> generates 4 sections in parallel (~15-27s) and streams them progressively to the frontend.</li>
                <li><strong>MCP Server</strong> (optional, gated by env var) exposes the same pipeline as tools for enterprise AI integration.</li>
              </ol>
            </TextContent>
          </Box>
        </div>

        {/* Pricing Section */}
        <div id="pricing">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <Header
              variant="h1"
              description="Indicative monthly cost in Asia Pacific (Sydney, ap-southeast-2) using on-demand pricing pulled from the AWS Pricing API."
            >
              Pricing
            </Header>
          </Box>

          <Alert statusIconAriaLabel="Info" header="How to read these numbers">
            These figures are a <strong>high-level planning guide</strong>, not a quote.
            Actual spend will depend on session volume, document size, prompt length,
            agent retry behaviour and Knowledge Base configuration. All prices are in
            USD and reflect published on-demand rates at time of writing; validate with
            the{' '}
            <Link external href="https://calculator.aws/">
              AWS Pricing Calculator
            </Link>{' '}
            before committing to a budget.
          </Alert>

          <Box margin={{ top: "l" }}>
            <Header variant="h2">Unit pricing (Sydney, on-demand)</Header>
          </Box>

          <Table
            variant="embedded"
            columnDefinitions={[
              { id: 'service', header: 'Service', cell: i => i.service, width: 240 },
              { id: 'unit', header: 'Unit', cell: i => i.unit },
              { id: 'price', header: 'USD', cell: i => i.price, width: 140 },
              { id: 'notes', header: 'Notes', cell: i => i.notes },
            ]}
            items={[
              {
                service: 'Amazon Bedrock — Nova Pro',
                unit: '1K input tokens',
                price: '$0.00084',
                notes: 'Orchestration + answer generation.',
              },
              {
                service: 'Amazon Bedrock — Nova Pro',
                unit: '1K output tokens',
                price: '$0.00336',
                notes: 'Agent responses; 4× input rate.',
              },
              {
                service: 'Bedrock Knowledge Base',
                unit: 'Managed retrieval',
                price: 'Pass-through',
                notes: 'KB is fully managed. Cost is the sum of the underlying OpenSearch Serverless, S3 and Titan embeddings it provisions on your behalf.',
              },
              {
                service: 'OpenSearch Serverless',
                unit: 'OCU-hour',
                price: '$0.281',
                notes: 'Minimum 2 OCUs (1 indexing + 1 search) always running for a KB. 2 OCU × 24 × 30 ≈ 1,440 OCU-hrs/mo.',
              },
              {
                service: 'OpenSearch Serverless storage',
                unit: 'GB-month',
                price: '$0.026',
                notes: 'Vector index storage on S3.',
              },
              {
                service: 'AWS Fargate — vCPU',
                unit: 'vCPU-hour',
                price: '$0.04856',
                notes: 'Runs the Strands agent container.',
              },
              {
                service: 'AWS Fargate — memory',
                unit: 'GB-hour',
                price: '$0.00532',
                notes: 'Billed alongside vCPU.',
              },
              {
                service: 'Amazon DynamoDB (on-demand)',
                unit: 'Million read requests',
                price: '$0.1425',
                notes: 'Session + API state.',
              },
              {
                service: 'Amazon DynamoDB (on-demand)',
                unit: 'Million write requests',
                price: '$0.71',
                notes: '5× read rate; keep session writes minimal.',
              },
              {
                service: 'Amazon DynamoDB storage',
                unit: 'GB-month',
                price: '$0.285',
                notes: 'First 25 GB free.',
              },
              {
                service: 'Amazon S3 — Standard',
                unit: 'GB-month',
                price: '$0.025',
                notes: 'Source documents for the Knowledge Base.',
              },
            ]}
          />

          <Box margin={{ top: "xl" }}>
            <Header variant="h2">Scenario: light demo workload</Header>
            <Box variant="p" color="text-body-secondary" margin={{ top: "xs", bottom: "l" }}>
              100 research sessions per month, 6 questions per session, ~8K input + ~2K
              output Nova Pro tokens per question, 500 MB of indexed documents and a
              single Fargate task running 40 hours a month (demo hours only).
            </Box>
          </Box>

          <Table
            variant="embedded"
            columnDefinitions={[
              { id: 'service', header: 'Service', cell: i => i.service, width: 260 },
              { id: 'calc', header: 'Calculation', cell: i => i.calc },
              { id: 'cost', header: 'Monthly cost', cell: i => i.cost, width: 160 },
            ]}
            items={[
              {
                service: 'Nova Pro — input',
                calc: '100 × 6 × 8,000 = 4.8M tokens → 4,800 × $0.00084',
                cost: '≈ $4.03',
              },
              {
                service: 'Nova Pro — output',
                calc: '100 × 6 × 2,000 = 1.2M tokens → 1,200 × $0.00336',
                cost: '≈ $4.03',
              },
              {
                service: 'OpenSearch Serverless (2 OCUs)',
                calc: '2 OCUs × 24 × 30 = 1,440 hrs × $0.281',
                cost: '≈ $404.64',
              },
              {
                service: 'OpenSearch Serverless storage',
                calc: '0.5 GB × $0.026',
                cost: '≈ $0.01',
              },
              {
                service: 'Fargate (0.5 vCPU, 1 GB, 40 hrs)',
                calc: '40 × (0.5 × $0.04856 + 1 × $0.00532)',
                cost: '≈ $1.18',
              },
              {
                service: 'DynamoDB (on-demand)',
                calc: 'Well under 1M R/W + <25 GB (free tier)',
                cost: '< $1',
              },
              {
                service: 'S3 (0.5 GB source docs)',
                calc: '0.5 GB × $0.025',
                cost: '≈ $0.01',
              },
              {
                service: 'Indicative total',
                calc: 'Dominated by the 24×7 OpenSearch Serverless minimum.',
                cost: '≈ $415 USD / month',
              },
            ]}
          />

          <Box margin={{ top: "l" }}>
            <Header variant="h3">Where the cost sits</Header>
          </Box>
          <TextContent>
            <ul>
              <li>
                <strong>OpenSearch Serverless is the largest line at low
                volume.</strong> The two-OCU minimum is approximately
                US$405 per month regardless of query traffic, so the
                marginal cost of an additional session is small until the
                OCUs are saturated.
              </li>
              <li>
                <strong>You can replace OpenSearch Serverless with{' '}
                <Link external href="https://aws.amazon.com/s3/features/vectors/">
                  Amazon S3 Vectors
                </Link>{' '}
                as the Knowledge Base vector store.</strong> S3 Vectors
                charges only for the vector data you store and the queries
                you run, with no minimum capacity commitment. For workloads
                with modest query volume this typically removes most of the
                OpenSearch floor and materially reduces the monthly bill.
              </li>
              <li>
                <strong>Nova Pro scales linearly with session volume.</strong>{' '}
                At approximately US$0.08 per session in this scenario, ten
                times the sessions adds roughly US$80 per month — still
                modest relative to the Knowledge Base running cost.
              </li>
              <li>
                <strong>Fargate is a minor line at demo hours.</strong>{' '}
                Running the container only when required, either through
                scheduled tasks or by setting the service minimum capacity
                to zero, keeps compute spend well under US$5 per month.
              </li>
            </ul>
          </TextContent>
        </div>

        {/* How we sourced these numbers */}
        <div id="how-we-sourced-these-numbers">
          <Box margin={{ top: "xl", bottom: "l" }}>
            <Header
              variant="h2"
              description="These figures were pulled live from the AWS Price List API, not copied from a spreadsheet."
            >
              How did we get these numbers?
            </Header>
          </Box>
          <TextContent>
            <p>
              Every price on this page was queried directly from the AWS
              Price List API through the{' '}
              <strong>AWS Pricing MCP Server</strong>, an open-source tool
              from AWS Labs that exposes pricing data as a set of callable
              tools for any MCP-aware assistant or IDE (for example, Amazon
              Q Developer, Claude Desktop or Kiro).
            </p>
            <p>The server provides four practical capabilities:</p>
            <ul>
              <li>
                <strong>Discover service codes and attributes</strong> so
                you can find the right filter fields for a given service
                (for example, <code>inferenceType</code> and{' '}
                <code>model</code> for Amazon Bedrock).
              </li>
              <li>
                <strong>Query on-demand, batch and reserved pricing</strong>{' '}
                by region, with multi-region comparison in a single call.
              </li>
              <li>
                <strong>Analyse existing projects</strong> — point it at a
                CDK or Terraform project and it will enumerate the AWS
                services in use and estimate their cost.
              </li>
              <li>
                <strong>Generate cost reports</strong> in markdown or CSV,
                including calculation breakdowns and assumptions, suitable
                for sharing with finance or architecture review boards.
              </li>
            </ul>
            <p>
              Using the MCP server means the numbers here are reproducible
              — anyone with access to the same tool can re-run the queries
              and confirm the rates, and the figures update automatically
              whenever AWS revises its pricing.
            </p>
            <p>
              Full documentation and installation instructions:{' '}
              <Link external href="https://awslabs.github.io/mcp/servers/aws-pricing-mcp-server">
                awslabs.github.io/mcp/servers/aws-pricing-mcp-server
              </Link>.
            </p>
          </TextContent>
        </div>
      </SpaceBetween>
    </ContentLayout>
  );
}

// Tools component for "On this page" navigation
function ArchitecturePageTools() {
  return (
    <div>
      <Header variant="h3">
        <span id="navigation-header">On this page</span>
      </Header>
      <AnchorNavigation
        anchors={[
          { text: "Overview", href: "#overview", level: 1 },
          { text: "Pricing", href: "#pricing", level: 1 },
          { text: "How we sourced these numbers", href: "#how-we-sourced-these-numbers", level: 1 },
        ]}
        ariaLabelledby="navigation-header"
      />
    </div>
  );
}

// Main ArchitecturePage component with page-specific layout
function ArchitecturePage() {
  return (
    <AppLayout
      content={<ArchitecturePageContent />}
      tools={<ArchitecturePageTools />}
      toolsHide={false}
      navigationHide={true}
    />
  );
}

export default ArchitecturePage;
